import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { FilterPopover } from '@/components/common/FilterPopover/FilterPopover';

const DashboardHeaderSlotContext = createContext(null);

/** Maps dashboard “critical alert” rows into header notification list items. */
export function mapCriticalAlertsToHeaderNotifications(alerts) {
    return (Array.isArray(alerts) ? alerts : []).map((a) => ({
        id: a.id,
        title: a.task || 'Alert',
        subtitle: a.pillarName,
        meta:
            typeof a.healthScore === 'number'
                ? `${Math.round(a.healthScore)}% health`
                : undefined,
        kind: 'critical',
    }));
}

/**
 * Escalations, coordination, and other operational notices — distinct from critical health alerts.
 * Expects flattened rows: `{ id, task, pillarName, tag? }` (`task` is the main message line).
 */
export function mapEscalationNoticesToHeaderNotifications(items) {
    return (Array.isArray(items) ? items : []).map((e) => ({
        id: e.id,
        title: e.task || e.message || 'Notice',
        subtitle: e.pillarName,
        meta: e.tag,
        kind: 'escalation',
    }));
}

/** Concatenates notification lists (e.g. critical + escalation) for the header bell. */
export function mergeDashboardNotifications(...groups) {
    const out = [];
    for (const g of groups) {
        if (Array.isArray(g)) out.push(...g);
    }
    return out;
}

/**
 * Optional second argument: `{ filterActive?: boolean }` — shows a dot on the filter icon when true.
 */
export function DashboardHeaderSlotProvider({ children }) {
    const [slot, setSlot] = useState(null);
    const [slotMeta, setSlotMeta] = useState({});
    const [unreadNotifications, setUnreadNotifications] = useState([]);
    /** When set from the header bell, `ExecutiveFormatDashboard` switches to Critical, scrolls, and highlights the card. */
    const [criticalAlertBellFocus, setCriticalAlertBellFocus] = useState(null);
    const setDashboardHeaderSlot = useCallback((node, meta = {}) => {
        setSlot(node);
        setSlotMeta(node == null ? {} : meta);
    }, []);
    const requestCriticalAlertBellFocus = useCallback((id) => {
        if (id == null || String(id).trim() === '') return;
        setCriticalAlertBellFocus({ id: String(id), nonce: Date.now() });
    }, []);
    const clearCriticalAlertBellFocus = useCallback(() => setCriticalAlertBellFocus(null), []);
    const value = useMemo(
        () => ({
            slot,
            slotMeta,
            setDashboardHeaderSlot,
            unreadNotifications,
            setUnreadNotifications,
            criticalAlertBellFocus,
            requestCriticalAlertBellFocus,
            clearCriticalAlertBellFocus,
        }),
        [
            slot,
            slotMeta,
            setDashboardHeaderSlot,
            unreadNotifications,
            criticalAlertBellFocus,
            requestCriticalAlertBellFocus,
            clearCriticalAlertBellFocus,
        ],
    );
    return (
        <DashboardHeaderSlotContext.Provider value={value}>
            {children}
        </DashboardHeaderSlotContext.Provider>
    );
}

export function useDashboardHeaderSlot() {
    const ctx = useContext(DashboardHeaderSlotContext);
    if (!ctx) {
        return {
            slot: null,
            slotMeta: {},
            setDashboardHeaderSlot: () => {},
            unreadNotifications: [],
            setUnreadNotifications: () => {},
            criticalAlertBellFocus: null,
            requestCriticalAlertBellFocus: () => {},
            clearCriticalAlertBellFocus: () => {},
        };
    }
    return ctx;
}


/**
 * Dropdown toggle for switching dashboard views (e.g. Project / Task).
 * `config`: `{ value, onChange, options: [{ value, label, icon? }] }`.
 */
function HeaderViewToggle({ config }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const active = config.options.find((o) => o.value === config.value);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} className="relative flex items-center">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-semibold text-foreground shadow-sm transition-colors hover:border-primary-300 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring/40"
                aria-expanded={open}
                aria-haspopup="listbox"
            >
                {active?.icon ? <span className="flex-shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">{active.icon}</span> : null}
                <span className="truncate">{active?.label ?? 'View'}</span>
                <ChevronDown className={`h-3 w-3 flex-shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            <div
                className={`absolute right-0 top-full z-[60] mt-1.5 min-w-[10rem] origin-top-right rounded-lg border border-border bg-card py-1 shadow-lg transition-all ${
                    open ? 'visible scale-100 opacity-100' : 'invisible scale-95 opacity-0 pointer-events-none'
                }`}
                role="listbox"
                aria-label="Dashboard view"
            >
                {config.options.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        role="option"
                        aria-selected={o.value === config.value}
                        onClick={() => { config.onChange(o.value); setOpen(false); }}
                        className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-primary-50 dark:hover:bg-primary-950/40 ${
                            o.value === config.value
                                ? 'font-semibold text-primary-700 dark:text-primary-300'
                                : 'text-muted-foreground'
                        }`}
                    >
                        {o.icon ? <span className="flex-shrink-0 [&>svg]:h-4 [&>svg]:w-4">{o.icon}</span> : null}
                        {o.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

/**
 * Renders the filter sidebar (and optional view toggle) registered by dashboard pages.
 * Pages call `setDashboardHeaderSlot(sections, { filterActive, viewToggle? })` where
 * `sections` is `Array<{ id, label, content, active? }>` and
 * `viewToggle` is `{ value, onChange, options: [{ value, label, icon? }] }`.
 */
export function DashboardHeaderSlot() {
    const { slot, slotMeta } = useDashboardHeaderSlot();
    const hasFilters = Array.isArray(slot) && slot.length > 0;
    const hasViewToggle = Boolean(slotMeta.viewToggle);
    if (!hasFilters && !hasViewToggle) return null;
    return (
        <div className="flex items-center gap-2">
            {hasViewToggle ? <HeaderViewToggle config={slotMeta.viewToggle} /> : null}
            {hasFilters ? <FilterPopover sections={slot} active={Boolean(slotMeta.filterActive)} onReset={slotMeta.onReset ?? null} /> : null}
        </div>
    );
}
